import { flow, getParent, type Instance, types } from 'mobx-state-tree';

import { deleteApplicationAttachment } from 'src/graphql/hirer/deleteApplicationAttachment';
import { fetchApplicationAttachments } from 'src/graphql/hirer/fetchApplicationAttachments';

import {
  HIRER_UPLOADED,
  isApplicationDocuments,
} from '../../constants/attachmentTypes';
import { addAttachmentService } from '../../services/attachmentService';
import {
  trackAddedAttachment,
  trackDeleteAttachment,
} from '../../utils/events';
import { logError } from '../../utils/logError';
import {
  ACTION_DONE,
  ACTION_ERROR,
  ACTION_VALIDATION_ERROR,
  STATE_ERROR,
  STATE_LOADED,
  stateType,
} from '../state';

import Attachment, { type IAttachment } from './Attachment';
import type { IJobApplication } from './JobApplication';

const AttachmentsStore = types
  .model('Attachments', {
    attachmentList: types.array(Attachment),
    state: stateType,
  })
  .views((self) => ({
    get jobApplication(): IJobApplication {
      return getParent(self) as IJobApplication;
    },
  }))
  .views((self) => ({
    get hirerUploadedDocuments() {
      return self.attachmentList
        .filter(
          (attachment: IAttachment) => attachment.type === HIRER_UPLOADED.type,
        )
        .sort((attachment1: IAttachment, attachment2: IAttachment) =>
          (attachment2.uploadedDate || '').localeCompare(
            attachment1.uploadedDate || '',
          ),
        );
    },
    get applicationDocuments() {
      return self.attachmentList.filter((attachment: IAttachment) =>
        isApplicationDocuments(attachment.type),
      );
    },
  }))
  .actions((self) => {
    const loadAttachments: any = flow(function* () {
      try {
        const attachments = yield fetchApplicationAttachments(
          (self.jobApplication as IJobApplication).id,
        );
        self.attachmentList = attachments;
        self.state = STATE_LOADED;
      } catch (error: any) {
        self.state = STATE_ERROR;
        logError(
          {
            errorCode: 'LOAD_ATTACHMENTS',
            description: 'Error when attempting: fetchApplicationAttachments',
          },
          {
            error,
          },
        );
      }
    });

    const addAttachment: any = flow(function* (file: File) {
      try {
        self.attachmentList = (yield addAttachmentService(
          self.jobApplication.id,
          file,
        )) as any;
        trackAddedAttachment(self.jobApplication.baseTrackingAttributes);
        return ACTION_DONE;
      } catch (error: any) {
        logError(
          {
            errorCode: 'ADD_ATTACHMENT',
            description: 'Error when attempting: addAttachmentService',
          },
          {
            error,
          },
        );
        const limitErrorMessage = 'Prospect attachment limit';
        if (
          error.message.length === 0 ||
          error.message.indexOf(limitErrorMessage) > -1
        ) {
          return ACTION_VALIDATION_ERROR;
        }
        return ACTION_ERROR;
      }
    });

    const deleteAttachment: any = flow(function* (attachmentId) {
      try {
        const result = yield deleteApplicationAttachment(
          self.jobApplication.id,
          attachmentId,
        );
        if (!result) {
          throw new Error('attachment deletion api return false');
        }
        self.attachmentList.forEach((attachment: IAttachment) => {
          if (attachment.id === attachmentId) {
            self.attachmentList.remove(attachment);
          }
        });
        trackDeleteAttachment(self.jobApplication.baseTrackingAttributes);
        return ACTION_DONE;
      } catch (error: any) {
        logError(
          {
            errorCode: 'DELETE_ATTACHMENT',
            description: 'Error when attempting: deleteApplicationAttachment',
          },
          {
            error,
          },
        );
        return ACTION_ERROR;
      }
    });
    return {
      loadAttachments,
      addAttachment,
      deleteAttachment,
    };
  });

export interface IAttachmentsStore extends Instance<typeof AttachmentsStore> {}

export default AttachmentsStore;
